import type { HqApi, ScreenDeviceRow } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';

export interface ScreenDeviceListProps {
  /** 已登录会话的 SDK 实例（各端注入，复用其 baseUrl 与令牌）。 */
  hq: HqApi;
  /** 平台/运营端传租户 id 只看某个租户；GOV/HOSPITAL 不传（后端按令牌锁定本机构）。 */
  tenantId?: string;
  /**
   * 是否显示「吊销」操作。
   * SYSTEM/OPS 可吊销本租户全部设备；HOSPITAL/GOV 只能吊销本机构（含下属机构）范围内的设备，
   * 与后端 `ScreenDeviceService.DisableAsync` 的范围校验口径一致。
   */
  canDisable?: boolean;
  /** 列表上方的说明文案（各端措辞不同）。 */
  hint?: string;
}

/**
 * 已授权大屏列表（各端「我的大屏」）。
 * 只展示凭据前缀，不展示明文；SYSTEM/OPS 可逐台吊销，吊销后大屏下一次换票即失败。
 * 数据范围完全由后端按令牌（门户 + 租户 + 机构子树）裁剪，前端不做二次过滤。
 */
export function ScreenDeviceList({ hq, tenantId, canDisable = false, hint }: ScreenDeviceListProps) {
  const queryClient = useQueryClient();
  const queryKey = ['screen-device', tenantId ?? 'all'] as const;

  const list = useQuery({
    queryKey,
    queryFn: () => hq.tenant.screenDevices(tenantId)
  });

  const disable = useMutation({
    mutationFn: (id: string) => hq.tenant.disableScreenDevice(id),
    onSuccess: async () => {
      message.success('已吊销，该大屏下次换票即失效');
      await queryClient.invalidateQueries({ queryKey: ['screen-device'] });
    },
    onError: (err: unknown) => message.error(err instanceof Error ? err.message : '吊销失败')
  });

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          hint ??
          '申请码授权产生：大屏首次打开显示 12 位申请码，在电脑端「申请码确认」填入即可（system/ops 可搜索筛选机构，监管/医院管理人员自动为本机构），确认后大屏自动领取凭据。'
        }
      />
      <Table<ScreenDeviceRow>
        size="small"
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: '暂无已授权大屏' }}
        columns={[
          { title: '业务', dataIndex: 'bizName', width: 130 },
          { title: '大屏名称', dataIndex: 'deviceName', width: 180, render: (value: string | null) => value ?? '—' },
          {
            title: '显示范围',
            width: 200,
            render: (_, row) => (row.orgId === '0' ? '全区（监管视角）' : row.orgName ?? row.orgId)
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (value: string) =>
              value === 'ISSUED' ? (
                <Tag color="green">已发放</Tag>
              ) : value === 'AUTHORIZED' ? (
                <Tag color="gold">待领取</Tag>
              ) : (
                <Tag>已吊销</Tag>
              )
          },
          { title: '凭据前缀', dataIndex: 'devicePrefix', width: 130, render: (value: string | null) => value ?? '—' },
          {
            title: '最近换票',
            dataIndex: 'lastSeenAt',
            width: 170,
            render: (value: string | null) => (value ? formatDisplayTime(value) : '—')
          },
          ...(canDisable
            ? [
                {
                  title: '操作',
                  width: 100,
                  key: 'action',
                  render: (_: unknown, row: ScreenDeviceRow) =>
                    row.status === 'DISABLED' ? (
                      <Typography.Text type="secondary">已吊销</Typography.Text>
                    ) : (
                      <Space size={4}>
                        <Popconfirm
                          title="吊销该大屏？"
                          description="吊销后该大屏将无法继续取数，需要在大屏上重新申请、再输入申请码授权。"
                          okText="吊销"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => disable.mutate(row.id)}
                        >
                          <Button size="small" danger type="link">
                            吊销
                          </Button>
                        </Popconfirm>
                      </Space>
                    )
                }
              ]
            : [])
        ]}
      />
    </div>
  );
}
